import { useState, useCallback, useEffect } from 'react';
import { useApi, useToaster } from '../../../../utils/hooks';
import { ToastStyle } from '../../../../utils/hooks/useToaster';
import {
  IWorkHoursDto,
  IWorkHoursAddRequest,
  IWorkHoursAddResult,
  IWorkHoursUpdateResult,
  IWorkHoursDeleteResult,
  ISystemCodeByTypeDto
} from '../../../../utils/constants/interfaces';
import { SortDirection } from '../../../../utils/constants/enums';
import { Helpers } from '../../../../utils/helpers';

type WorkHoursFormMode = 'disabled' | 'add' | 'edit';

export type WorkHoursOrderBy = 'workDate' | 'enteredDate' | 'updatedDate';

interface UseWorkHoursProps {
  clubMemberId: string;
  fieldErrors: string[];
  setFieldErrors: React.Dispatch<React.SetStateAction<string[]>>;
  committees: ISystemCodeByTypeDto[];
}

export const useWorkHours = ({
  clubMemberId,
  fieldErrors,
  setFieldErrors,
  committees
}: UseWorkHoursProps) => {
  const api = useApi();
  const toaster = useToaster();

  // State for work hours
  const [workHours, setWorkHours] = useState<IWorkHoursDto[]>([]);
  const [loadingWorkHours, setLoadingWorkHours] = useState<boolean>(false);
  const [savingWorkHours, setSavingWorkHours] = useState<boolean>(false);
  const [workHoursFormMode, setWorkHoursFormMode] = useState<WorkHoursFormMode>('disabled');
  const [editingWorkHoursId, setEditingWorkHoursId] = useState<number | null>(null);
  const [selectedWorkHoursRow, setSelectedWorkHoursRow] = useState<number | null>(null);

  // Work hours filtering and sorting
  const [workHoursYearFilter, setWorkHoursYearFilter] = useState<string>('All');
  const [workHoursOrderBy, setWorkHoursOrderByState] = useState<WorkHoursOrderBy>('workDate');
  const [workHoursSortField, setWorkHoursSortField] = useState<string>('workHoursDateUtc');
  const [workHoursSortDirection, setWorkHoursSortDirection] = useState<SortDirection>(
    SortDirection.DESCENDING
  );

  const [workHoursFormData, setWorkHoursFormData] = useState<{
    workHoursDateUtc: string;
    workHoursAmount: string;
    activity: string;
    committeeId: string;
  }>({
    workHoursDateUtc: Helpers.toDateString(new Date()),
    workHoursAmount: '',
    activity: '',
    committeeId: ''
  });

  // Fetch work hours from API
  const fetchWorkHours = useCallback(
    async (memberId: string): Promise<void> => {
      if (!memberId || memberId === 'new') {
        setWorkHours([]);
        return;
      }

      setLoadingWorkHours(true);

      try {
        const endpoint = `/members/${memberId}/workhours`;
        const apiResponse = await api.get<IWorkHoursDto[]>(endpoint);

        if (apiResponse?.status?.statusCode === 200 && apiResponse.response) {
          // Process dates in work hours data using the safer Helpers.convertToDate method
          const processedWorkHours = apiResponse.response
            .map((wh) => {
              const workHoursDate = Helpers.convertToDate(wh.workHoursDateUtc);
              const createdDate = Helpers.convertToDate(wh.dateCreatedUtc);
              const modifiedDate = Helpers.convertToDate(wh.lastModifiedDateUtc);

              return {
                ...wh,
                workHoursDateUtc: workHoursDate || new Date(),
                dateCreatedUtc: createdDate || new Date(),
                lastModifiedDateUtc: modifiedDate || new Date()
              };
            })
            .filter((wh) => {
              // Filter out any entries where critical dates couldn't be parsed
              const hasValidWorkDate = wh.workHoursDateUtc && !isNaN(wh.workHoursDateUtc.getTime());
              const hasValidCreatedDate = wh.dateCreatedUtc && !isNaN(wh.dateCreatedUtc.getTime());
              return hasValidWorkDate && hasValidCreatedDate;
            });

          setWorkHours(processedWorkHours);
        } else {
          setWorkHours([]);
          toaster.addToast('Error fetching work hours', ToastStyle.ERROR, 3000);
        }
      } catch (error) {
        setWorkHours([]);
        toaster.addToast('Error fetching work hours', ToastStyle.ERROR, 3000);
      } finally {
        setLoadingWorkHours(false);
      }
    },
    [api, toaster]
  );

  // Load work hours when club member ID changes
  useEffect(() => {
    if (clubMemberId && clubMemberId !== 'new') {
      void fetchWorkHours(clubMemberId);
    } else {
      setWorkHours([]);
    }
    // fetchWorkHours is stable but we only want this to run when clubMemberId changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubMemberId]);

  const updateWorkHoursFormData = (field: string, value: string) => {
    if (workHoursFormMode === 'disabled') return;

    setWorkHoursFormData((prev) => ({
      ...prev,
      [field]: value
    }));

    // Clear related field errors when user updates data
    const errorKey = `workhours:${field}`;
    setFieldErrors((prev) => prev.filter((error) => error !== errorKey));
  };

  const clearWorkHoursForm = () => {
    setWorkHoursFormData({
      workHoursDateUtc: Helpers.toDateString(new Date()),
      workHoursAmount: '',
      activity: '',
      committeeId: ''
    });
    setWorkHoursFormMode('disabled');
    setEditingWorkHoursId(null);
    setSelectedWorkHoursRow(null);
    // Clear all workhours related field errors
    setFieldErrors((prev) => prev.filter((error) => !error.startsWith('workhours:')));
  };

  const enableAddMode = () => {
    setWorkHoursFormData({
      workHoursDateUtc: Helpers.toDateString(new Date()),
      workHoursAmount: '',
      activity: '',
      committeeId: ''
    });
    setWorkHoursFormMode('add');
    setEditingWorkHoursId(null);
    setSelectedWorkHoursRow(null);
    setFieldErrors((prev) => prev.filter((error) => !error.startsWith('workhours:')));
  };

  const enableEditMode = (workHoursItem: IWorkHoursDto, rowIndex: number) => {
    // Populate form with existing data
    setWorkHoursFormData({
      workHoursDateUtc: Helpers.toDateString(workHoursItem.workHoursDateUtc),
      workHoursAmount: workHoursItem.workHoursAmount.toString(),
      activity: workHoursItem.activity,
      committeeId: workHoursItem.committeeId.toString()
    });
    setWorkHoursFormMode('edit');
    setEditingWorkHoursId(workHoursItem.id);
    setSelectedWorkHoursRow(rowIndex);
    setFieldErrors((prev) => prev.filter((error) => !error.startsWith('workhours:')));
  };

  const validateWorkHoursForm = (): boolean => {
    const errors: string[] = [];

    if (Helpers.isNullOrWhiteSpace(workHoursFormData.workHoursDateUtc)) {
      errors.push('workhours:workHoursDateUtc');
    }

    // Validate hours - must be a positive number in half-hour increments (club
    // policy: events are tracked to the half hour); no max limit, matching the Java app.
    const hoursValue = parseFloat(workHoursFormData.workHoursAmount);
    const isHalfHourIncrement = Math.abs(hoursValue * 2 - Math.round(hoursValue * 2)) < 1e-9;
    if (isNaN(hoursValue) || hoursValue <= 0 || !isHalfHourIncrement) {
      errors.push('workhours:workHoursAmount');
    }

    if (Helpers.isNullOrWhiteSpace(workHoursFormData.committeeId)) {
      errors.push('workhours:committeeId');
    }

    if (Helpers.isNullOrWhiteSpace(workHoursFormData.activity)) {
      errors.push('workhours:activity');
    }

    // Add errors to existing field errors
    setFieldErrors((prev) => [
      ...prev.filter((error) => !error.startsWith('workhours:')),
      ...errors
    ]);
    return errors.length === 0;
  };

  const saveWorkHours = async (): Promise<void> => {
    if (savingWorkHours || !clubMemberId || clubMemberId === 'new') return;

    const isValid = validateWorkHoursForm();
    if (!isValid) {
      toaster.addToast('Please fill in all required fields correctly', ToastStyle.ERROR, 3000);
      return;
    }

    setSavingWorkHours(true);

    try {
      if (workHoursFormMode === 'add') {
        // Create new work hours entry
        const endpoint = `/members/${clubMemberId}/workhours`;
        const workHoursData: IWorkHoursAddRequest = {
          clubMemberId: parseInt(clubMemberId),
          workHoursDateUtc: new Date(workHoursFormData.workHoursDateUtc + 'T12:00:00.000Z'),
          workHoursAmount: parseFloat(workHoursFormData.workHoursAmount),
          activity: workHoursFormData.activity,
          committeeId: parseInt(workHoursFormData.committeeId)
        };

        const apiResponse = await api.post<IWorkHoursAddResult>(
          endpoint,
          JSON.stringify(workHoursData)
        );

        if (apiResponse?.status?.statusCode === 200 || apiResponse?.status?.statusCode === 201) {
          toaster.addToast('Work hours added successfully', ToastStyle.SUCCESS, 3000);
          await fetchWorkHours(clubMemberId);
          clearWorkHoursForm();
        } else {
          toaster.addToast(
            `Error adding work hours: ${apiResponse?.errors?.[0]?.description || 'Unknown error'}`,
            ToastStyle.ERROR,
            5000
          );
        }
      } else if (workHoursFormMode === 'edit') {
        // Update existing work hours entry
        const endpoint = `/members/${clubMemberId}/workhours/${editingWorkHoursId}`;

        // Find the current committee object for the WorkHoursDto
        const selectedCommittee = committees.find(
          (c) => c.id === parseInt(workHoursFormData.committeeId)
        );

        const workHoursData: IWorkHoursDto = {
          id: editingWorkHoursId!,
          fullName: '', // Will be set by the server
          clubMemberId: parseInt(clubMemberId),
          workHoursDateUtc: new Date(workHoursFormData.workHoursDateUtc + 'T12:00:00.000Z'),
          workHoursAmount: parseFloat(workHoursFormData.workHoursAmount),
          activity: workHoursFormData.activity,
          committeeId: parseInt(workHoursFormData.committeeId),
          committee: selectedCommittee || null,
          createdBy: '', // Will be set by the server
          dateCreatedUtc: new Date(), // Will be set by the server
          lastModifiedBy: '', // Will be set by the server
          lastModifiedDateUtc: new Date() // Will be set by the server
        };

        const apiResponse = await api.put<IWorkHoursUpdateResult>(
          endpoint,
          JSON.stringify(workHoursData)
        );

        if (apiResponse?.status?.statusCode === 200) {
          toaster.addToast('Work hours updated successfully', ToastStyle.SUCCESS, 3000);
          await fetchWorkHours(clubMemberId);
          clearWorkHoursForm();
        } else {
          toaster.addToast(
            `Error updating work hours: ${apiResponse?.errors?.[0]?.description || 'Unknown error'}`,
            ToastStyle.ERROR,
            5000
          );
        }
      }
    } catch (error) {
      toaster.addToast(
        `Error ${workHoursFormMode === 'add' ? 'adding' : 'updating'} work hours`,
        ToastStyle.ERROR,
        5000
      );
    } finally {
      setSavingWorkHours(false);
    }
  };

  // Soft-deletes the entry currently loaded in the edit form.
  const deleteWorkHours = async (): Promise<void> => {
    if (savingWorkHours || !clubMemberId || clubMemberId === 'new') return;
    if (workHoursFormMode !== 'edit' || !editingWorkHoursId) return;

    setSavingWorkHours(true);

    try {
      const endpoint = `/members/${clubMemberId}/workhours/${editingWorkHoursId}`;
      const apiResponse = await api.delete<IWorkHoursDeleteResult>(endpoint);

      if (apiResponse?.status?.statusCode === 200 && apiResponse.response?.succeeded) {
        toaster.addToast('Work hours entry deleted', ToastStyle.SUCCESS, 3000);
        await fetchWorkHours(clubMemberId);
        clearWorkHoursForm();
      } else {
        toaster.addToast(
          `Error deleting work hours: ${
            apiResponse?.response?.message ||
            apiResponse?.errors?.[0]?.description ||
            'Unknown error'
          }`,
          ToastStyle.ERROR,
          5000
        );
      }
    } catch (error) {
      toaster.addToast('Error deleting work hours', ToastStyle.ERROR, 5000);
    } finally {
      setSavingWorkHours(false);
    }
  };

  // Get available years from work hours for filter dropdown
  const getAvailableYears = (): string[] => {
    const years = workHours
      .map((wh) => wh.workHoursDateUtc.getFullYear().toString())
      .filter((year, index, arr) => arr.indexOf(year) === index)
      .sort((a, b) => parseInt(b) - parseInt(a)); // Sort descending
    return ['All', ...years];
  };

  // Get total hours for a specific year
  const getYearlyTotal = (year: number): number => {
    return workHours
      .filter((wh) => wh.workHoursDateUtc.getFullYear() === year)
      .reduce((sum, wh) => sum + wh.workHoursAmount, 0);
  };

  // Get current year and previous year totals
  const getCurrentYearTotal = (): number => {
    const currentYear = new Date().getFullYear();
    return getYearlyTotal(currentYear);
  };

  const getPreviousYearTotal = (): number => {
    const previousYear = new Date().getFullYear() - 1;
    return getYearlyTotal(previousYear);
  };

  // Handle work hours sorting
  const handleWorkHoursSort = (sortField: string) => {
    if (workHoursSortField === sortField) {
      // Toggle sort direction if same field
      setWorkHoursSortDirection(
        workHoursSortDirection === SortDirection.ASCENDING
          ? SortDirection.DESCENDING
          : SortDirection.ASCENDING
      );
    } else {
      // New field, start with ascending
      setWorkHoursSortField(sortField);
      setWorkHoursSortDirection(SortDirection.ASCENDING);
    }
  };

  // Handle order by dropdown change
  const setWorkHoursOrderBy = (orderBy: WorkHoursOrderBy) => {
    setWorkHoursOrderByState(orderBy);

    // Set the corresponding sort field and direction (all descending)
    switch (orderBy) {
      case 'workDate':
        setWorkHoursSortField('workHoursDateUtc');
        break;
      case 'enteredDate':
        setWorkHoursSortField('dateCreatedUtc');
        break;
      case 'updatedDate':
        setWorkHoursSortField('lastModifiedDateUtc');
        break;
    }
    setWorkHoursSortDirection(SortDirection.DESCENDING);
  };

  // Get filtered and sorted work hours for display
  const getFilteredAndSortedWorkHours = (): IWorkHoursDto[] => {
    let filtered = workHours;

    // Apply year filter
    if (workHoursYearFilter !== 'All') {
      const filterYear = parseInt(workHoursYearFilter);
      filtered = workHours.filter((wh) => wh.workHoursDateUtc.getFullYear() === filterYear);
    }

    // Apply sorting
    return filtered.sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (workHoursSortField) {
        case 'workHoursDateUtc':
          aValue = a.workHoursDateUtc.getTime();
          bValue = b.workHoursDateUtc.getTime();
          break;
        case 'workHoursAmount':
          aValue = a.workHoursAmount;
          bValue = b.workHoursAmount;
          break;
        case 'committee':
          aValue = a.committee?.description || '';
          bValue = b.committee?.description || '';
          break;
        case 'activity':
          aValue = a.activity.toLowerCase();
          bValue = b.activity.toLowerCase();
          break;
        case 'createdBy':
          aValue = a.createdBy.toLowerCase();
          bValue = b.createdBy.toLowerCase();
          break;
        case 'dateCreatedUtc':
          aValue = a.dateCreatedUtc.getTime();
          bValue = b.dateCreatedUtc.getTime();
          break;
        case 'lastModifiedDateUtc':
          aValue = a.lastModifiedDateUtc.getTime();
          bValue = b.lastModifiedDateUtc.getTime();
          break;
        case 'lastModifiedBy':
          aValue = a.lastModifiedBy.toLowerCase();
          bValue = b.lastModifiedBy.toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return workHoursSortDirection === SortDirection.ASCENDING ? -1 : 1;
      }
      if (aValue > bValue) {
        return workHoursSortDirection === SortDirection.ASCENDING ? 1 : -1;
      }
      return 0;
    });
  };

  return {
    // State
    workHours,
    loadingWorkHours,
    savingWorkHours,
    workHoursFormData,
    workHoursFormMode,
    selectedWorkHoursRow,
    workHoursYearFilter,
    workHoursOrderBy,
    workHoursSortField,
    workHoursSortDirection,

    // Actions
    updateWorkHoursFormData,
    saveWorkHours,
    deleteWorkHours,
    clearWorkHoursForm,
    enableAddMode,
    enableEditMode,
    setWorkHoursYearFilter,
    setWorkHoursOrderBy,
    handleWorkHoursSort,

    // Computed values
    getAvailableYears,
    getYearlyTotal,
    getCurrentYearTotal,
    getPreviousYearTotal,
    getFilteredAndSortedWorkHours
  };
};
